import { createListAtoms } from "../../common/DataList";

// ============================================================
// Écran Sanctions France Travail — configuration pure des deux listes
// (testable sans DOM) : filtres par défaut, définitions des champs,
// construction des requêtes.
// L'API n'expose pas de route de constantes pour ces filtres : les libellés
// reprennent ceux du Domain (StatutPropositionSanctionFT, MotifRejetSanctionFT,
// StatutDeclarationSanctionCAF). Ce ne sont que des options de filtre — les
// libellés affichés dans les tableaux viennent toujours de l'API.
// ============================================================

export const PROPOSITIONS_ENDPOINT = "france-travail/sanctions/search";
export const DECLARATIONS_ENDPOINT = "france-travail/sanctions/declarations/search";
export const relancerPropositionEndpoint = (idConsequenceSanction) =>
  `france-travail/sanctions/${idConsequenceSanction}/relancer`;
export const relancerDeclarationEndpoint = (inscriptionEPId) =>
  `france-travail/sanctions/declarations/${inscriptionEPId}/relancer`;
// Relance en une fois de tout ce qui est en échec ; l'API rend { relancees, ignorees, echouees }.
export const RELANCER_PROPOSITIONS_EN_ECHEC_ENDPOINT = "france-travail/sanctions/relancer-en-echec";
export const RELANCER_DECLARATIONS_EN_ECHEC_ENDPOINT = "france-travail/sanctions/declarations/relancer-en-echec";

export const STATUTS_PROPOSITION = [
  { id: "EN_ATTENTE", name: "En attente" },
  { id: "INSCRITE_EN_EP", name: "Inscrite en EP" },
  { id: "DECISION_REMONTEE", name: "Décision remontée" },
  { id: "REJETEE", name: "Rejetée avant EP" },
  { id: "LEVEE_A_TRAITER", name: "Levée de suspension à traiter" },
  { id: "LEVEE_REPONDUE", name: "Levée de suspension répondue" },
];

export const MOTIFS_REJET = [
  { id: "HORS_BAREME", name: "Hors barème CAF" },
  { id: "ALLOCATAIRE_INCONNU", name: "Allocataire inconnu" },
  { id: "NON_SANCTIONNABLE", name: "BRSA non sanctionnable" },
  { id: "REMOBILISATION_ANTICIPEE", name: "Remobilisation acceptée avant décision" },
];

// « Rien à déclarer » (l'EP n'a pas sanctionné) et « Non transmise à France
// Travail » (la sanction existe, FT ne peut pas la recevoir) sont deux issues
// closes distinctes : la seconde est ce que la Caf traite à la main (ST1-2209).
export const STATUTS_DECLARATION = [
  { id: "BLOQUEE", name: "Déclaration bloquée" },
  { id: "DECLAREE", name: "Sanction déclarée" },
  { id: "RADIATION_DEMANDEE", name: "Radiation demandée" },
  { id: "NON_TRANSMISSIBLE", name: "Non transmise à France Travail" },
  { id: "NON_DECLARABLE", name: "Rien à déclarer" },
];

export const PROPOSITIONS_DEFAULT_FILTERS = {
  terms: "",
  statut: "",
  motifRejet: "",
  dateDebut: "",
  dateFin: "",
  dateDecisionDebut: "",
  dateDecisionFin: "",
  enEchec: false,
};

export const DECLARATIONS_DEFAULT_FILTERS = {
  terms: "",
  statut: "",
  dateDebut: "",
  dateFin: "",
  dateDecisionDebut: "",
  dateDecisionFin: "",
  enEchec: false,
};

// Période de finalisation de l'EP (validation du PV) : la Caf retrouve ce
// qu'elle vient de décider, quel que soit le jour de réception ou de
// déclaration (ST1-2209). Mêmes clés sur les deux listes.
const FILTRE_PERIODE_DECISION = {
  name: "decision",
  group: "decision",
  label: "Décidée en EP",
  type: "dateRange",
  from: "dateDecisionDebut",
  to: "dateDecisionFin",
  fromLabel: "EP finalisée à partir du",
  toLabel: "EP finalisée jusqu'au",
};

// La dernière recherche est mémorisée entre deux visites (comme l'écran
// historique le faisait) ; chaque onglet garde la sienne.
export const PROPOSITIONS_PERSIST_KEY = "espoar.boa.sanctionsFT.propositions.search";
export const DECLARATIONS_PERSIST_KEY = "espoar.boa.sanctionsFT.declarations.search";

export const propositionsAtoms = createListAtoms({
  defaultFilters: PROPOSITIONS_DEFAULT_FILTERS,
  persistKey: PROPOSITIONS_PERSIST_KEY,
});
export const declarationsAtoms = createListAtoms({
  defaultFilters: DECLARATIONS_DEFAULT_FILTERS,
  persistKey: DECLARATIONS_PERSIST_KEY,
});

export const PROPOSITIONS_FILTER_GROUPS = [
  { id: "suivi", title: "Suivi" },
  { id: "reception", title: "Réception" },
  { id: "decision", title: "Décision de l'EP" },
];

// Le statut est le filtre principal (barre de recherche) ; les dates de
// réception forment une période. Mêmes clés envoyées à l'API.
export const PROPOSITIONS_FILTER_DEFINITIONS = [
  {
    name: "statut",
    group: "suivi",
    label: "Statut",
    type: "select",
    principal: true,
    firstOptionLabel: "Tous",
    options: STATUTS_PROPOSITION,
  },
  {
    name: "motifRejet",
    group: "suivi",
    label: "Motif de rejet",
    type: "select",
    size: "lg",
    firstOptionLabel: "Tous",
    options: MOTIFS_REJET,
  },
  {
    name: "enEchec",
    group: "suivi",
    label: "En échec uniquement",
    chipLabel: "En échec",
    type: "checkbox",
  },
  {
    name: "reception",
    group: "reception",
    label: "Reçue",
    type: "dateRange",
    from: "dateDebut",
    to: "dateFin",
    fromLabel: "Reçue à partir du",
    toLabel: "Reçue jusqu'au",
  },
  FILTRE_PERIODE_DECISION,
];

export const DECLARATIONS_FILTER_GROUPS = [
  { id: "suivi", title: "Suivi" },
  { id: "declaration", title: "Déclaration" },
  { id: "decision", title: "Décision de l'EP" },
];

export const DECLARATIONS_FILTER_DEFINITIONS = [
  {
    name: "statut",
    group: "suivi",
    label: "Statut",
    type: "select",
    principal: true,
    firstOptionLabel: "Tous",
    options: STATUTS_DECLARATION,
  },
  {
    name: "enEchec",
    group: "suivi",
    label: "En échec uniquement",
    chipLabel: "En échec",
    type: "checkbox",
  },
  {
    name: "declaration",
    group: "declaration",
    label: "Déclarée",
    type: "dateRange",
    from: "dateDebut",
    to: "dateFin",
    fromLabel: "Déclarée à partir du",
    toLabel: "Déclarée jusqu'au",
  },
  FILTRE_PERIODE_DECISION,
];

const text = (value) => (value === undefined || value === null ? "" : value);

// Corps des requêtes : mêmes clés et mêmes formats que l'API attend (chaînes
// venant du DOM, booléen pour la case à cocher).
export function buildPropositionsPayload(search) {
  const f = { ...PROPOSITIONS_DEFAULT_FILTERS, ...(search.filters || {}) };
  return {
    filters: {
      statut: text(f.statut),
      motifRejet: text(f.motifRejet),
      terms: text(f.terms),
      dateDebut: text(f.dateDebut),
      dateFin: text(f.dateFin),
      dateDecisionDebut: text(f.dateDecisionDebut),
      dateDecisionFin: text(f.dateDecisionFin),
      enEchec: f.enEchec === true,
    },
    page: Number(search.page) || 1,
    nbItemsByPage: Number(search.nbItemsByPage) || 25,
  };
}

export function buildDeclarationsPayload(search) {
  const f = { ...DECLARATIONS_DEFAULT_FILTERS, ...(search.filters || {}) };
  return {
    filters: {
      statut: text(f.statut),
      terms: text(f.terms),
      dateDebut: text(f.dateDebut),
      dateFin: text(f.dateFin),
      dateDecisionDebut: text(f.dateDecisionDebut),
      dateDecisionFin: text(f.dateDecisionFin),
      enEchec: f.enEchec === true,
    },
    page: Number(search.page) || 1,
    nbItemsByPage: Number(search.nbItemsByPage) || 25,
  };
}
